import { useState, useCallback } from 'react'
import services from '@services'
import mcpAxios from '@services/mcpApi/axios'

const { RestService } = services

export interface ChatMessage {
  role: 'user' | 'assistant'
  text: string
}

export interface ScheduleMenu {
  menu_id: string
  menu_name: string
  chef_id: string
  chef_name: string
  cuisines: string[]
}

export interface ScheduleDay {
  date: string
  day: string
  menus: ScheduleMenu[]
}

export interface ScheduleJson {
  client: string
  month: string
  schedule: ScheduleDay[]
}

import { DateMenus } from '../../../../types/groupOrder/dateMenus'

export interface ConceptSummary {
  id: string
  name: string
  chefId: string
  chefName: string
  cuisines: string[]
  avgPricePerPerson: number | null
  chefRating: number
  menuItemCount: number
  isUniversal: boolean
  groupOrderAvailDays: number[]
  dietaryPreferenceTags?: string[]
  groupOrderCutoffDays?: number | null
  groupOrderCutoffTime?: number | null
  groupOrderCutoffIncludesWeekends?: boolean
}

interface AcceptScheduleResult {
  created: { date: string; dateMenuId: string }[]
  errors: { date: string; menuId?: string; message: string }[]
  previews?: DateMenus[]
  dateMenus?: DateMenus[]
}

export function useMenuGeneration() {
  const [chatId, setChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [scheduleJson, setScheduleJson] = useState<ScheduleJson | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState<string>('Thinking...')
  const [isAccepting, setIsAccepting] = useState(false)
  const [acceptResult, setAcceptResult] = useState<AcceptScheduleResult | null>(
    null,
  )
  const [acceptProgress, setAcceptProgress] = useState<{
    current: number
    total: number
    dateStr: string
  } | null>(null)
  const [conceptMap, setConceptMap] = useState<Record<
    string,
    ConceptSummary
  > | null>(null)
  const [error, setError] = useState<string | null>(null)

  const createChat = useCallback(
    async (userId: string, clientSettingsId: string) => {
      try {
        setError(null)
        const response = await mcpAxios.post('/scheduling/chats', {
          user_id: userId,
          client_settings_id: clientSettingsId,
        })
        const data = response.data
        setChatId(data.chat_id)
        return data.chat_id
      } catch (e: any) {
        setError(e?.response?.data?.detail || e.message)
        return null
      }
    },
    [],
  )

  const sendMessage = useCallback(
    async (message: string) => {
      if (!chatId) return

      setIsLoading(true)
      setLoadingStatus('Thinking...')
      setError(null)

      // Optimistically add user message
      setMessages((prev) => [...prev, { role: 'user', text: message }])

      try {
        // SSE stream requires raw fetch — axios doesn't support ReadableStream
        const mcpBaseUrl = mcpAxios.defaults.baseURL || ''
        const token = window.localStorage.getItem('accessToken')
        const response = await fetch(
          `${mcpBaseUrl}/scheduling/chats/${chatId}/messages/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ message }),
          },
        )
        if (!response.ok) throw new Error('Failed to send message')

        const reader = response.body?.getReader()
        if (!reader) throw new Error('No response stream')

        const decoder = new TextDecoder()
        let buffer = ''
        let doneData: {
          assistant_text: string
          schedule_json: ScheduleJson | null
        } | null = null

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || '' // Keep incomplete event in buffer

          for (const part of parts) {
            if (!part.trim() || part.startsWith(':')) continue // Skip heartbeats

            let eventType = ''
            let eventData = ''
            for (const line of part.split('\n')) {
              if (line.startsWith('event: ')) eventType = line.slice(7)
              else if (line.startsWith('data: ')) eventData = line.slice(6)
            }

            if (!eventType || !eventData) continue

            const parsed = JSON.parse(eventData)

            switch (eventType) {
              case 'status':
                setLoadingStatus(parsed.message)
                break
              case 'tool_call':
                setLoadingStatus(`Using tool: ${parsed.tool}...`)
                break
              case 'tool_result':
                setLoadingStatus('Thinking...')
                break
              case 'assistant_text':
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant', text: parsed.text },
                ])
                break
              case 'done':
                doneData = parsed
                break
              case 'error':
                throw new Error(parsed.message)
            }
          }
        }

        if (!doneData) throw new Error('No response received from stream')

        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: doneData!.assistant_text },
        ])

        if (doneData.schedule_json) {
          setScheduleJson(doneData.schedule_json)

          // Fetch concept summaries for dietary tags & cutoff info
          const allMenuIds = new Set<string>()
          for (const day of doneData.schedule_json.schedule) {
            for (const menu of day.menus) {
              allMenuIds.add(menu.menu_id)
            }
          }
          if (allMenuIds.size > 0) {
            try {
              const conceptData = await RestService.post(
                '/group-orders/agent/concepts',
                { conceptIds: Array.from(allMenuIds) },
              )
              setConceptMap(conceptData.concepts)
            } catch {
              // Non-critical — table still works without concept data
            }
          }
        }
      } catch (e: any) {
        setError(e.message)
        // Remove optimistic message on error
        setMessages((prev) => prev.slice(0, -1))
      } finally {
        setIsLoading(false)
        setLoadingStatus('Thinking...')
      }
    },
    [chatId],
  )

  const [isCreating, setIsCreating] = useState(false)
  const [createResult, setCreateResult] = useState<AcceptScheduleResult | null>(
    null,
  )
  const [createProgress, setCreateProgress] = useState<{
    current: number
    total: number
    dateStr: string
  } | null>(null)

  const acceptSchedule = useCallback(
    async (clientSettingsId: string) => {
      if (!scheduleJson) return

      setIsAccepting(true)
      setError(null)
      setAcceptResult(null)
      setAcceptProgress(null)

      try {
        const schedule = scheduleJson.schedule.map((day) => ({
          date: day.date,
          menuIds: day.menus.map((m) => m.menu_id),
          active: false,
        }))

        const allPreviews: DateMenus[] = []
        const allErrors: { date: string; menuId?: string; message: string }[] =
          []

        for (let i = 0; i < schedule.length; i++) {
          setAcceptProgress({
            current: i + 1,
            total: schedule.length,
            dateStr: schedule[i].date,
          })

          try {
            const result: AcceptScheduleResult = await RestService.post(
              '/group-orders/date-menus/bulk-create-from-schedule',
              { clientSettingsId, schedule: [schedule[i]], dryRun: true },
            )
            allPreviews.push(...(result.previews || []))
            allErrors.push(...(result.errors || []))
          } catch (e: any) {
            const errorMsg =
              e?.response?.data?.message ||
              e.message ||
              `Failed for ${schedule[i].date}`
            allErrors.push({ date: schedule[i].date, message: errorMsg })
          }
        }

        const aggregated: AcceptScheduleResult = {
          previews: allPreviews,
          errors: allErrors,
          created: [],
        }
        setAcceptResult(aggregated)
        return aggregated
      } catch (e: any) {
        const message =
          e?.response?.data?.message || e.message || 'Failed to accept schedule'
        setError(message)
        return null
      } finally {
        setIsAccepting(false)
        setAcceptProgress(null)
      }
    },
    [scheduleJson],
  )

  const createDateMenus = useCallback(
    async (clientSettingsId: string, activeMap: Record<string, boolean>) => {
      if (!scheduleJson) return

      setIsCreating(true)
      setError(null)
      setCreateResult(null)
      setCreateProgress(null)

      try {
        const schedule = scheduleJson.schedule
          // Exclude dates that failed the preview (dryRun) step — only dates
          // with successful previews have entries in activeMap.
          .filter((day) => day.date in activeMap)
          .map((day) => ({
            date: day.date,
            menuIds: day.menus.map((m) => m.menu_id),
            active: !!activeMap[day.date],
          }))

        const allCreated: { date: string; dateMenuId: string }[] = []
        const allErrors: { date: string; menuId?: string; message: string }[] =
          []

        for (let i = 0; i < schedule.length; i++) {
          setCreateProgress({
            current: i + 1,
            total: schedule.length,
            dateStr: schedule[i].date,
          })

          try {
            const result: AcceptScheduleResult = await RestService.post(
              '/group-orders/date-menus/bulk-create-from-schedule',
              { clientSettingsId, schedule: [schedule[i]], dryRun: false },
            )
            allCreated.push(...(result.created || []))
            allErrors.push(...(result.errors || []))
          } catch (e: any) {
            const errorMsg =
              e?.response?.data?.message ||
              e.message ||
              `Failed for ${schedule[i].date}`
            allErrors.push({ date: schedule[i].date, message: errorMsg })
          }
        }

        const aggregated: AcceptScheduleResult = {
          created: allCreated,
          errors: allErrors,
        }
        setCreateResult(aggregated)
        return aggregated
      } catch (e: any) {
        const message =
          e?.response?.data?.message ||
          e.message ||
          'Failed to create date menus'
        setError(message)
        return null
      } finally {
        setIsCreating(false)
        setCreateProgress(null)
      }
    },
    [scheduleJson],
  )

  return {
    chatId,
    messages,
    scheduleJson,
    conceptMap,
    isLoading,
    loadingStatus,
    isAccepting,
    acceptResult,
    acceptProgress,
    isCreating,
    createResult,
    createProgress,
    error,
    createChat,
    sendMessage,
    acceptSchedule,
    createDateMenus,
  }
}
